import React, { useEffect, useMemo, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { getSupportDriverDetail } from "../../../../api/adminSupportApi";

function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}

const BRAND = { pink: "#FF2D83", ink: "#0B0E13" };

function IconChevronLeft({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
      <path d="M14 7l-5 5 5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconAudit({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
      <path
        d="M9 11h6M9 15h6M8 3h8l3 3v15a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path d="M16 3v4h4" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    </svg>
  );
}

export default function SupportDriverLayout() {
  const { driverId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [detail, setDetail] = useState(null);

  // If route is exactly /admin/support/drivers/:driverId, push to overview
  useEffect(() => {
    const path = location.pathname || "";
    const endsAtDriver = path.endsWith(`/drivers/${driverId}`) || path.endsWith(`/drivers/${driverId}/`);
    if (driverId && endsAtDriver) navigate("overview", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driverId]);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!driverId) return;
      setLoading(true);
      setErr("");
      try {
        const d = await getSupportDriverDetail(driverId);
        if (!alive) return;
        setDetail(d);
      } catch (e) {
        if (!alive) return;
        setErr(e?.message || "Failed to load driver");
        setDetail(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [driverId]);

  const tabs = useMemo(
    () => [
      { to: "overview", label: "Overview" },
      { to: "bookings", label: "Bookings" },
      { to: "rides", label: "Rides" },
      { to: "notes", label: "Notes" },
    ],
    []
  );

  if (loading) return <div className="text-sm font-semibold text-slate-600">Loading…</div>;
  if (err) return <div className="text-sm font-semibold text-rose-600">{err}</div>;
  if (!detail) return <div className="text-sm font-semibold text-slate-600">No data</div>;

  return (
    <div className="min-w-0 space-y-4">
      {/* Header card (MATCH Owner/User style) */}
      <div className="rounded-2xl border border-slate-200 bg-white">
        <div className="flex flex-col gap-3 px-5 py-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-3">
              <span className="h-2 w-2 shrink-0" style={{ background: BRAND.pink, borderRadius: 3 }} aria-hidden="true" />
              <div className="min-w-0">
                <div className="truncate text-[18px] font-extrabold tracking-tight text-slate-900">
                  Driver Support <span className="text-slate-400">•</span> ID {driverId}
                </div>
                <div className="mt-1 text-[13px] font-medium text-slate-500">
                  Review activity and manage support actions for this driver.
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
            <Link
              to="/admin/support/drivers"
              className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-900 hover:bg-slate-50"
            >
              <IconChevronLeft className="h-4 w-4 text-slate-700" />
              Back to search
            </Link>

            <button
              type="button"
              onClick={() => navigate(`/admin/audit?entityType=drivers&entityId=${encodeURIComponent(driverId)}`)}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-slate-900 bg-slate-900 px-4 text-sm font-semibold text-white hover:opacity-95"
            >
              <IconAudit className="h-4 w-4 text-white" />
              View audit
            </button>
          </div>
        </div>

        {/* Tabs (MATCH Owner/User style) */}
        <div className="border-t border-slate-200 px-3 py-3">
          <div className="flex flex-wrap gap-2">
            {tabs.map((t) => (
              <NavLink
                key={t.to}
                to={t.to}
                className={({ isActive }) =>
                  classNames(
                    "inline-flex h-10 items-center rounded-xl border px-4 text-sm font-semibold transition",
                    isActive
                      ? "border-slate-900 bg-slate-900 text-white"
                      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                  )
                }
                end
              >
                {t.label}
              </NavLink>
            ))}
          </div>
        </div>
      </div>

      <Outlet context={{ driverId, driver: detail }} />
    </div>
  );
}
